import { RentChargeStatusOption } from '@/graphql/generated';

export type RentChargeReportLike = {
  rentChargeStatusOption?: RentChargeStatusOption;
  amountValue?: string | number;
  executionDate?: string | number | Date | null;
  dueDate?: string | number | Date | null;
};

const RECEIVED_STATUS = RentChargeStatusOption.Received;

const parseAmount = (value: string | number | undefined): number => {
  if (value == null) return 0;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : 0;
};

const isInMonth = (date: Date | null, month: number, year: number): boolean => {
  if (!date || Number.isNaN(date.getTime())) return false;
  return date.getMonth() === month && date.getFullYear() === year;
};

const isChargeDueOrExecutedInMonth = (
  charge: RentChargeReportLike,
  month: number,
  year: number
): boolean => {
  const execDate = charge.executionDate ? new Date(charge.executionDate) : null;
  const dueDate = charge.dueDate ? new Date(charge.dueDate) : null;
  return isInMonth(execDate, month, year) || isInMonth(dueDate, month, year);
};

export type RentsThisMonthBreakdown = {
  charged: number;
  notCharged: number;
  total: number;
};

export const getRentsThisMonthFromReport = (
  charges: RentChargeReportLike[] | null | undefined,
  month?: number,
  year?: number
): RentsThisMonthBreakdown => {
  if (!charges?.length) {
    return { charged: 0, notCharged: 0, total: 0 };
  }
  const now = new Date();
  const targetMonth = month ?? now.getMonth();
  const targetYear = year ?? now.getFullYear();

  let charged = 0;
  let notCharged = 0;

  for (const charge of charges) {
    if (!isChargeDueOrExecutedInMonth(charge, targetMonth, targetYear)) continue;
    const amount = parseAmount(charge.amountValue);
    if (charge.rentChargeStatusOption === RECEIVED_STATUS) {
      charged += amount;
    } else {
      notCharged += amount;
    }
  }

  return {
    charged,
    notCharged,
    total: charged + notCharged,
  };
};

export const formatIncomeAmount = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};
